// The server clock. Every countdown and "done at" is computed from the game server's time, not the device's:
// village/get carries server_time (unix seconds); the offset is the median of the last few samples.
import { useEffect, useState } from "react";
import { create } from "zustand";

interface ClockState {
  offsetMs: number;
  samples: number[];
  synced: boolean;
  sample: (serverMs: number, sentAtMs?: number) => void;
}

export const useClock = create<ClockState>((set, get) => ({
  offsetMs: 0,
  samples: [],
  synced: false,
  sample: (serverMs, sentAtMs) => {
    // the answer carries whole seconds, so the true server time is within a second: lean half a second forward
    const estimate = serverMs + 500 - (sentAtMs ? (Date.now() - sentAtMs) / 2 : 0);
    const off = estimate - Date.now();
    const samples = [...get().samples.slice(-6), off];
    const sorted = [...samples].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    set({ samples, offsetMs: median, synced: true });
  },
}));

/** ms, on the game server's clock */
export const serverNow = () => Date.now() + useClock.getState().offsetMs;

/** Re-renders every `tickMs` and returns the server time. */
export function useNow(tickMs = 1000): number {
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    let id = 0;
    const tick = () => {
      setNow(serverNow());
      // align to the next whole tick so seconds flip together across the screen
      const delay = tickMs - ((Date.now() % tickMs) || 0);
      id = window.setTimeout(tick, delay);
    };
    id = window.setTimeout(tick, tickMs - (Date.now() % tickMs));
    return () => window.clearTimeout(id);
  }, [tickMs]);
  return now;
}
